import * as React from 'react';
import {
  boxSelectionDriverFactory,
  BoxSelectionDriver,
} from './BoxSelection.driver';
import { BoxSelection, BoxSelectionProps } from '.';
import { BoxSelectionBoxSize, BoxSelectionSelectionType } from './types';
import { createRendererWithUniDriver } from '../../test/utils';

describe('BoxSelection', () => {
  const render = createRendererWithUniDriver<BoxSelectionDriver>(
    boxSelectionDriverFactory,
  );

  const baseProps = {
    'data-hook': 'data-hook',
  };

  beforeEach(() => {
    // unset focus between tests
    (document.activeElement as any)?.blur?.();
  });

  const defProps: BoxSelectionProps = {
    'data-hook': 'data-hook',
    name: 'hours',
    size: BoxSelectionBoxSize.xLarge,
    'aria-label': '',
    'aria-labelledby': '',
    onChange: () => {},
  };

  const bootstrap = () => {
    return render(<BoxSelection {...defProps} />, baseProps['data-hook']);
  };

  it('should render', async () => {
    const { driver } = bootstrap();
    expect(await driver.exists()).toBe(true);
  });

  it('should show checked state', async () => {
    const optionId = '1';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id={optionId} checked />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    expect(await driver.isChecked(optionId)).toBeTruthy();
  });

  it('should be able to focus on an option by a direct ref to the option', async () => {
    const optionToFocusId = 'option-with-ref';
    const optionRef = React.createRef<any>();
    render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id="not-the-one" />
        <BoxSelection.Option id={optionToFocusId} ref={optionRef} />
        <BoxSelection.Option id="not-the-one-too" />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    expect(document.activeElement.id).not.toBe(optionToFocusId);

    optionRef.current.focus();

    expect(document.activeElement.id).toBe(optionToFocusId);
  });

  it('should be able to focus on an option by index from the box selection container', async () => {
    const optionIdInDesiredIndex = 'option-in-index';
    const desiredOptionIndex = 1;
    const boxRef = React.createRef<any>();
    render(
      <BoxSelection {...defProps} name="hours" ref={boxRef}>
        <BoxSelection.Option id="not-the-one" />
        <BoxSelection.Option id={optionIdInDesiredIndex} />
        <BoxSelection.Option id="not-the-one-too" />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    boxRef.current.focus(desiredOptionIndex);

    expect(document.activeElement.id).toBe(optionIdInDesiredIndex);
  });

  it('should be able to focus on the currently selected option', async () => {
    const checkedOptionId = 'checked-option-id';
    const boxRef = React.createRef<any>();
    render(
      <BoxSelection {...defProps} name="hours" ref={boxRef}>
        <BoxSelection.Option id="not-the-one" />
        <BoxSelection.Option id={checkedOptionId} checked />
        <BoxSelection.Option id="not-the-one-too" />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    boxRef.current.focus();

    expect(document.activeElement.id).toBe(checkedOptionId);
  });

  it('should focus on the first option when no index is provided and no option is checked', async () => {
    const firstOptionId = 'first-option-id';
    const boxRef = React.createRef<any>();
    render(
      <BoxSelection {...defProps} name="hours" ref={boxRef}>
        <BoxSelection.Option id={firstOptionId} />
        <BoxSelection.Option id="not-the-one" />
        <BoxSelection.Option id="not-the-one-too" />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    expect(document.activeElement.id).not.toBe(firstOptionId);

    boxRef.current.focus();

    expect(document.activeElement.id).toBe(firstOptionId);
  });

  it('should be able to focus on an option by a direct ref to a specific option in checkbox mode (single option)', async () => {
    const optionId = 'single-option-id';
    const ref = React.createRef<any>();
    render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id={optionId} checked ref={ref} />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    ref.current.focus();

    expect(document.activeElement.id).toBe(optionId);
  });

  it('should be able to focus on a single option from the container', async () => {
    const optionId = 'single-option-id';
    const boxRef = React.createRef<any>();
    render(
      <BoxSelection {...defProps} name="hours" ref={boxRef}>
        <BoxSelection.Option id={optionId} checked />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    boxRef.current.focus();

    expect(document.activeElement.id).toBe(optionId);
  });

  it('should indicate of 3 options', async () => {
    const expectedOptionLength = 3;
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id="1" />
        <BoxSelection.Option id="2" />
        <BoxSelection.Option id="3" />
      </BoxSelection>,
      baseProps['data-hook'],
    );

    expect(await driver.getOptionsCount()).toBe(expectedOptionLength);
  });

  it('should show disabled state', async () => {
    const optionId = '1';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id={optionId} disabled />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.isDisabled(optionId)).toBeTruthy();
  });

  it('should indicate unavailable state', async () => {
    const optionId = '1';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id={optionId} unavailable />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.isUnavailable(optionId)).toBeTruthy();
  });

  it('should render option as checkbox if only 1 item is available', async () => {
    const expectedOptionId = 'option-id';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id={expectedOptionId} />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getNativeSelector(expectedOptionId)).toBe(
      BoxSelectionSelectionType.CHECKBOX,
    );
  });

  it('should render option as checkbox if only 1 item is available with multiple items', async () => {
    const expectedOptionId = 'option-id';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id="1" disabled />
        <BoxSelection.Option id="2" disabled />
        <BoxSelection.Option id={expectedOptionId} />
        <BoxSelection.Option id="3" disabled />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getNativeSelector(expectedOptionId)).toBe(
      BoxSelectionSelectionType.CHECKBOX,
    );
  });

  it('should not render radio button as checkbox if all items are available', async () => {
    const expectedOptionId = 'option-id';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id="1" />
        <BoxSelection.Option id="2" />
        <BoxSelection.Option id="3" />
        <BoxSelection.Option id={expectedOptionId} />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getNativeSelector(expectedOptionId)).toBe(
      BoxSelectionSelectionType.RADIO_BUTTON,
    );
  });

  it('should render option as checkbox if BoxSelection.Option type is checkBox', async () => {
    const expectedOptionId = 'option-id';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id="1" type={BoxSelectionSelectionType.CHECKBOX} />
        <BoxSelection.Option
          id={expectedOptionId}
          type={BoxSelectionSelectionType.CHECKBOX}
        />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getNativeSelector(expectedOptionId)).toBe(
      BoxSelectionSelectionType.CHECKBOX,
    );
  });

  it('should render option as radio button if BoxSelection.Option type is radioButton', async () => {
    const expectedOptionId = 'option-id';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option
          id={expectedOptionId}
          type={BoxSelectionSelectionType.RADIO_BUTTON}
        />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getNativeSelector(expectedOptionId)).toBe(
      BoxSelectionSelectionType.RADIO_BUTTON,
    );
  });

  it('should not render option as checkbox if all items are disabled', async () => {
    const expectedOptionId = 'option-id';
    const { driver } = render(
      <BoxSelection {...defProps} name="hours">
        <BoxSelection.Option id="1" disabled />
        <BoxSelection.Option id="2" disabled />
        <BoxSelection.Option id={expectedOptionId} disabled />
        <BoxSelection.Option id="3" disabled />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getNativeSelector(expectedOptionId)).toBe(
      BoxSelectionSelectionType.RADIO_BUTTON,
    );
  });

  it('should get correct error message in error state', async () => {
    const { driver } = render(
      <BoxSelection
        error
        errorMessage="error message"
        {...defProps}
        name="hours"
      >
        <BoxSelection.Option id="1" />
        <BoxSelection.Option id="2" />
        <BoxSelection.Option id="3" />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getErrorMessageText()).toBe('error message');
  });

  it('should support suffix', async () => {
    const { driver } = render(
      <BoxSelection {...defProps} name="hours" suffix="This is suffix">
        <BoxSelection.Option id="1" />
        <BoxSelection.Option id="2" />
        <BoxSelection.Option id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
      baseProps['data-hook'],
    );
    expect(await driver.getSuffix().exists()).toBe(true);
  });
});
